/**
 * useRFPJobStream hook for monitoring RFP document processing jobs
 * Similar to useProposalTemplateStream but for RFP document ingest/structure extraction
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { streamRFPJobProgress, RFPJobStreamCallbacks } from '../api/opportunities';

export type RFPJobStage = 
  | 'idle'
  | 'queued'
  | 'waiting'
  | 'parsing'
  | 'chunking'
  | 'indexing'
  | 'extracting'
  | 'processing'
  | 'generating'
  | 'complete'
  | 'completed'
  | 'failed'
  | 'error';

export interface RFPJobStreamState {
  stage: RFPJobStage;
  message: string;
  /** Secondary line from worker (parse stats, chunk counts, LLM/index hints) */
  detail: string | null;
  progress: number;
  jobStatus: string;
  jobType: string;
  /** Filename currently being processed (multi-file ingest / structure extraction) */
  currentArtifact: string | null;
  /** 0-based index and total count when backend sends them */
  artifactsProcessed: number | null;
  totalArtifacts: number | null;
  error: string | null;
  isStreaming: boolean;
  elapsed: number;
  jobId: string | null;
}

const initialStreamingState: RFPJobStreamState = {
  stage: 'idle',
  message: '',
  detail: null,
  progress: 0,
  jobStatus: '',
  jobType: '',
  currentArtifact: null,
  artifactsProcessed: null,
  totalArtifacts: null,
  error: null,
  isStreaming: false,
  elapsed: 0,
  jobId: null,
};

/**
 * Hook for streaming RFP document processing job progress with real-time updates
 */
export function useRFPJobStream() {
  const [state, setState] = useState<RFPJobStreamState>(initialStreamingState);
  const abortRef = useRef<(() => void) | null>(null);
  const isStartingRef = useRef(false);
  const startTimeRef = useRef<number>(0);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Update elapsed time during streaming
  useEffect(() => {
    if (!state.isStreaming) {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      return;
    }

    elapsedIntervalRef.current = setInterval(() => {
      if (startTimeRef.current > 0) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        setState(prev => ({
          ...prev,
          elapsed: Math.round(elapsed * 10) / 10, // Round to 1 decimal
        }));
      }
    }, 100); // Update every 100ms for smooth elapsed time

    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
    };
  }, [state.isStreaming]);

  const startStream = useCallback((
    opportunityId: string,
    jobId: string,
    projectId: string
  ) => {
    // Cancel any existing stream first
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }

    // Prevent duplicate concurrent requests
    if (isStartingRef.current) {
      return;
    }

    isStartingRef.current = true;
    startTimeRef.current = Date.now();

    // Reset state
    setState({
      ...initialStreamingState,
      isStreaming: true,
      stage: 'queued',
      message: 'Starting job monitoring...',
      detail: null,
      jobId: jobId,
      progress: 0,
    });

    // Start streaming
    const callbacks: RFPJobStreamCallbacks = {
      onStatus: (data: Record<string, unknown>) => {
        console.log('[useRFPJobStream] Status update received:', data);
        setState(prev => {
          const rawStage = String(data.stage ?? '');
          const normalizedStage =
            rawStage === 'completed' ? 'complete' : (rawStage as RFPJobStage) || 'processing';
          const ca = data.current_artifact;
          const ap = data.artifacts_processed;
          const ta = data.total_artifacts;
          const newState = {
            ...prev,
            stage: normalizedStage,
            message: (data.message as string) || prev.message,
            detail:
              data.detail !== undefined && data.detail !== null && String(data.detail).trim() !== ''
                ? String(data.detail)
                : prev.detail,
            progress: Math.max(0, Math.min(100, (data.progress as number) || prev.progress)), // Clamp between 0-100
            jobStatus: (data.job_status as string) || prev.jobStatus,
            jobType: (data.job_type as string) || prev.jobType,
            currentArtifact:
              ca !== undefined && ca !== null && String(ca).trim() !== ''
                ? String(ca)
                : prev.currentArtifact,
            artifactsProcessed:
              typeof ap === 'number'
                ? ap
                : ap !== undefined && ap !== null && !Number.isNaN(Number(ap))
                  ? Number(ap)
                  : prev.artifactsProcessed,
            totalArtifacts:
              typeof ta === 'number'
                ? ta
                : ta !== undefined && ta !== null && !Number.isNaN(Number(ta))
                  ? Number(ta)
                  : prev.totalArtifacts,
            elapsed: (data.elapsed as number) || prev.elapsed,
          };
          console.log('[useRFPJobStream] State updated:', { 
            stage: newState.stage, 
            progress: newState.progress, 
            message: newState.message 
          });
          return newState;
        });
      },

      onComplete: (data) => {
        setState(prev => ({
          ...prev,
          stage: 'complete',
          message: data.message || 'Job completed successfully',
          detail: null,
          progress: 100,
          isStreaming: false,
          elapsed: data.elapsed || prev.elapsed,
        }));
        abortRef.current = null;
        isStartingRef.current = false;
        if (elapsedIntervalRef.current) {
          clearInterval(elapsedIntervalRef.current);
          elapsedIntervalRef.current = null;
        }
      },

      onError: (error: Error) => {
        setState(prev => ({
          ...prev,
          stage: 'error',
          error: error.message,
          message: `Error: ${error.message}`,
          isStreaming: false,
        }));
        abortRef.current = null;
        isStartingRef.current = false;
        if (elapsedIntervalRef.current) {
          clearInterval(elapsedIntervalRef.current);
          elapsedIntervalRef.current = null;
        }
      },
    };

    (async () => {
      try {
        const abort = await streamRFPJobProgress(opportunityId, jobId, projectId, callbacks);
        abortRef.current = abort;
      } catch (error: any) {
        setState(prev => ({
          ...prev,
          stage: 'error',
          error: error.message,
          message: `Failed to start monitoring: ${error.message}`,
          isStreaming: false,
        }));
        isStartingRef.current = false;
      }
    })();
  }, []);

  const cancelStream = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    isStartingRef.current = false;
    if (elapsedIntervalRef.current) {
      clearInterval(elapsedIntervalRef.current);
      elapsedIntervalRef.current = null;
    }
    setState(prev => ({
      ...prev,
      isStreaming: false,
      stage: 'idle',
      message: 'Job monitoring cancelled',
    }));
  }, []);

  const reset = useCallback(() => {
    cancelStream();
    isStartingRef.current = false;
    startTimeRef.current = 0;
    setState(initialStreamingState);
  }, [cancelStream]);

  return {
    ...state,
    startStream,
    cancelStream,
    reset,
  };
}
